import { z } from "zod";

import { getPrettyCash } from "~/v2/utils/formatting";
import { WITHDRAW_REASONS } from "../common/const";

const INVALID_TYPE_ERROR_MSG = "Amount must be a number";

const removeNonNumeric = (str: string) => str.replace(/[^0-9.]+/g, "");

const zAmount = z
  .string()
  .refine(
    (str) => !Number.isNaN(Number(removeNonNumeric(str))),
    INVALID_TYPE_ERROR_MSG,
  )
  .refine(
    (str) => Number(removeNonNumeric(str)) >= 0,
    "Amount must be positive",
  );

const parsePercent = (value: string | undefined): number | undefined => {
  if (value === undefined || value === "") {
    return undefined;
  }

  // Keep the sign so negative input can be rejected (the amount transformer
  // strips `-`, but withholding must surface negatives as an error).
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") {
    return undefined;
  }

  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Withholding percentage as the transfer API expects it. The API rejects an
 * explicit `0` but accepts the field being absent, so any input that resolves
 * to 0% (a blank field included) must be omitted from the request body rather
 * than sent as `"0"`.
 */
export const getSubmittableWithholdingPct = (
  value: string | undefined,
): string | undefined => (parsePercent(value) ? value : undefined);

/**
 * IRA distribution fields. Each field is a non-optional
 * `string` that defaults to `""`, so an empty `{}` is structurally valid, but
 * the `superRefine` makes them effectively required whenever the `ira` object
 * is present:
 *
 * Because validation lives on this sub-schema, it only runs when a withdrawal
 * provides an `ira` object (IRA accounts seed it with `{}`); non-IRA transfers
 * omit it entirely and are unaffected.
 */
export const zIraDistribution = z
  .object({
    // Fields stay non-optional (output type is `string`), but each defaults to
    // an empty string so a seeded `ira: {}` is structurally valid instead of
    // hitting Zod's raw "Required" type error before `superRefine` runs.
    // Requiredness is enforced by the `superRefine` below, which lets us surface
    // friendly messages once the `ira` object is present.
    distribution_reason: z.string().default(""),
    fed_pct: z.string().default(""),
    state_pct: z.string().default(""),
  })
  .superRefine(({ distribution_reason, fed_pct, state_pct }, ctx) => {
    if (!distribution_reason) {
      ctx.addIssue({
        path: ["distribution_reason"],
        message: "Please select a withdrawal reason.",
        code: "custom",
      });
    }

    const fed = parsePercent(fed_pct);
    const state = parsePercent(state_pct);

    (
      [
        ["fed_pct", fed_pct, fed],
        ["state_pct", state_pct, state],
      ] as const
    ).forEach(([path, raw, value]) => {
      if (value === undefined) {
        // A blank field reads as 0% so it stays consistent with the "0"
        // placeholder, and 0% is valid. Input that is present but doesn't
        // parse (`10.5.5`, `.`) must be rejected rather than silently
        // collapsed to 0%, which would submit no withholding at all.
        if (raw !== "") {
          ctx.addIssue({
            path: [path],
            message: "Enter a valid percentage.",
            code: "custom",
          });
        }
        return;
      }

      if (value < 0) {
        ctx.addIssue({
          path: [path],
          message: "Withholding must be at least 0%.",
          code: "custom",
        });
      } else if (value > 100) {
        ctx.addIssue({
          path: [path],
          message: "Withholding cannot exceed 100%.",
          code: "custom",
        });
      }
    });

    // Only run the combined check once both fields are individually valid (a
    // positive percentage within range), so we don't stack a misleading
    // "combined" error on top of an existing field-level error.
    const isValidPct = (value: number | undefined): value is number =>
      value !== undefined && value > 0 && value <= 100;

    if (isValidPct(fed) && isValidPct(state) && fed + state > 100) {
      // Attach to the `ira` object root (rather than a specific field) so the
      // combined error surfaces as its own message instead of stacking under
      // the Federal field.
      ctx.addIssue({
        path: [],
        message: "Combined federal and state withholding cannot exceed 100%.",
        code: "custom",
      });
    }
  });

export const zTransferSchema = z.object({
  amount: zAmount,
  reason: z.enum(WITHDRAW_REASONS).optional(),
  reason_details: z.string().optional(),
  // IRA distribution data. Optional on the base schema (non-IRA transfers omit
  // it); when present its inner fields are validated by `zIraDistribution`.
  ira: zIraDistribution.optional(),
});

export type TransferSchema = z.infer<typeof zTransferSchema>;

export const withdrawableSuperRefinement = ({
  fields,
  refinementContext,
  withdrawable,
}: {
  fields: TransferSchema;
  refinementContext: z.RefinementCtx;
  withdrawable: number;
}) => {
  const amount = getCorrectAmount(fields.amount);

  if (amount > 0 && amount > withdrawable) {
    refinementContext.addIssue({
      path: ["amount"],
      message: `Cannot withdraw more than ${getPrettyCash(withdrawable, {
        acceptZero: true,
      })}.`,
      code: "custom",
    });
  }
};

/**
 * Parse the transfer form's string amount field into a number.
 *
 * @param amount the return of `watch("amount")` or submit payload `amount`
 */
export const getCorrectAmount = (
  amount: string | number | undefined,
): number => {
  if (!amount) {
    return 0;
  }

  if (typeof amount === "string") {
    const noCommas = removeNonNumeric(amount);
    return Number(noCommas);
  }

  return amount;
};
